import { useState, useCallback, useMemo } from 'react';
import { VacancyStatusButton } from './vacancyStatusButton';
import { VacancyStatusSelect } from './vacancyStatusSelect';
import { Flex, type SelectProps, type ButtonProps } from '@mantine/core';
import { VACANCY_STATUS_AVAILABLE_OPTIONS } from './constants';
import { useGetVacancyStatuses } from '@entities/vacancy';

type VacancyStatusProps = {
  status?: string;
  selectProps?: Omit<SelectProps, 'onChange' | 'value'>;
  buttonProps?: Omit<ButtonProps, 'onClick'> & {
    onClick?: () => void;
  };
  onStatusChange?: (newStatus: string) => void;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
};

export const VacancyStatus = ({
  status,
  selectProps,
  buttonProps,
  onStatusChange,
  disabled = false,
  loading = false,
  className,
}: VacancyStatusProps) => {
  const [isShowSelect, setIsShowSelect] = useState(false);

  const { data: vacancyStatuses, isLoading } = useGetVacancyStatuses();

  const handleButtonClick = useCallback(() => {
    if (disabled || loading || !status) {
      return;
    }

    buttonProps?.onClick?.();

    if (!disabled) {
      setIsShowSelect(true);
    }
  }, [buttonProps?.onClick, disabled, loading, status]);

  const handleStatusChange = useCallback(
    (newStatus: string) => {
      onStatusChange?.(newStatus);
      setIsShowSelect(false);
    },
    [onStatusChange]
  );

  const handleDropdownClose = useCallback(() => {
    setIsShowSelect(false);
  }, []);

  const handleSelectPropsChange = useCallback(
    (value: string | null) => {
      if (value) {
        handleStatusChange(value);
      }
    },
    [handleStatusChange]
  );

  const dataStatuses = useMemo(() => {
    if (!vacancyStatuses || !status) {
      return [];
    }

    return vacancyStatuses
      .filter(vacancyStatus =>
        VACANCY_STATUS_AVAILABLE_OPTIONS?.[status]?.includes(vacancyStatus.name)
      )
      .map(status => ({
        value: status.name,
        label: status.displayName,
      }));
  }, [vacancyStatuses?.length, status]);

  return (
    <Flex className={className}>
      {isShowSelect ? (
        <VacancyStatusSelect
          {...selectProps}
          data={dataStatuses}
          value={status}
          onChange={handleSelectPropsChange}
          onDropdownClose={handleDropdownClose}
          defaultDropdownOpened
          disabled={disabled}
          status={status}
        />
      ) : (
        <VacancyStatusButton
          {...buttonProps}
          status={status}
          onClick={handleButtonClick}
          disabled={disabled}
          loading={isLoading || loading}
        />
      )}
    </Flex>
  );
};
